<template>
  <div>
    <popup-hint hint="mashbills">
      <template v-slot:breadcrumbs>
        <breadcrumbs :back="[{text:'Mashing',to:{name:'ProductionMashing'}}]" home="Mash bills"/>
      </template>
      <template v-slot:top>
        Mash bills are templates or formulas for creating a mash.
        Though you can create a mash by specifying the inputs
        (like grain, yeast and additives) every time, a mash bill
        allows you to define this information once and then use it
        to fill the mash information automatically.
      </template>
      <template v-slot:bottom>
        Not only does a mash bill save you time, but it
          ensures consistency between mashes.
      </template>
      <template v-slot:actions="{close}">
        <v-btn @click="close()" :to="{name:'ProductionMashingMashBillsEditNew'}" color="primary" class="ml-4" small>Create</v-btn>
      </template>
    </popup-hint>

    <purtrak-table name="mashBills" objectType="mash bills" :defaultColumns="defaultColumns" :headers="headers" ref="table" show-select show-expand
                   listEndPoint="mashBills2" v-model="selected"
                   fields="id,name,active,duration,volume"
                   :listFilters="listFilters"
                   :item-class="mashBill => (mashBill.active ? '' : 'grey--text')"
                   @dblclick="mashBill => $router.push({name:'ProductionMashing',params:{mashBillId:mashBill.id}})"
                   @itemExpanded="({item, value}) => expandMashBill(item, value)"
                   title="Mash Bills"
                   :print-info="{pageSize:'portrait'}"
                   download-endpoint="mashBillExportCSV"
                   >
      <template v-slot:filter>
        <filter-menu include-reset @reset="filters.status='1';filters.search=''">
          <v-list-item>
            <filter-search label="Name" v-model="filters.search"/>
          </v-list-item>
          <v-list-item>
            <v-autocomplete label="Status" dense outlined :items="[{value:'2',text:'All'},{divider:true},{value:'1',text:'Active'},{value:'0',text:'Inactive'}]" v-model="filters.status"/>
          </v-list-item>
        </filter-menu>
        <template v-if="!$vuetify.breakpoint.mobile">
          <v-chip v-show="filters.search" close small class="ml-1" @click:close="filters.search=''">Mash bills: {{filters.search}}</v-chip>
          <v-chip v-show="filters.status!='1'" close small class="ml-1" @click:close="filters.status='1'">{{filters.status=='0'?'Inactive':'All'}}</v-chip>
        </template>
      </template>
      <template v-slot:menu v-if="!readOnly">
        <v-list-item :to="{name:'ProductionMashingMashBillsEditNew'}"><v-list-item-title>Create mash bill</v-list-item-title></v-list-item>
      </template>

      <template v-slot:header.action v-if="!readOnly">
        <v-btn fab color="primary" small :to="{name:'ProductionMashingMashBillsEditNew'}"><v-icon>mdi-plus</v-icon></v-btn>
      </template>
      <template v-slot:item.name="{item,printing}">
        <template v-if="printing">{{item.name}}</template>
        <template v-else>
          <v-hover v-if="!readOnly" v-slot="{hover}">
            <a @click.stop="$router.push({name:'ProductionMashingMashBillsEdit',params:{mashBillId:item.id}})"
               :class="{'text-decoration-underline':hover}">{{item.name}}</a>
          </v-hover>
          <template v-else>{{item.name}}</template>
          <div v-if="!item.active" class="grey--text text-caption">Inactive</div>
        </template>
      </template>
      <template v-slot:item.createmash="{item}">
        <v-btn v-if="item.active" rounded color="success" small :to="{name:'ProductionMashingEditNew',params:{mashBillId:item.id}}">Mash</v-btn>
      </template>
      <template v-slot:item.action="{item}">
        <v-menu :nudge-width="150" offset-y>
          <template v-slot:activator="{ on }">
            <v-btn small v-on="on" v-if="$vuetify.breakpoint.mobile" outlined class="ml-4">Mash bill operations</v-btn>
            <v-btn small icon v-on="on" v-else><v-icon small>mdi-dots-vertical</v-icon></v-btn>
          </template>
          <v-list dense>
            <template v-if="!readOnly">
              <v-list-item v-if="item.active" :to="{name:'ProductionMashingEditNew',params:{mashBillId:item.id}}"><v-list-item-title>Mash</v-list-item-title></v-list-item>
              <v-list-item @click="printMash(item.id, {}, true)"><v-list-item-title>Print</v-list-item-title></v-list-item>
              <v-divider/>
            </template>
            <v-list-item :to="{name:'ProductionMashing',query:{mashBillId:item.id}}"><v-list-item-title>Transactions</v-list-item-title></v-list-item>
            <v-list-item :to="{name:'ReportsView',params:{back:[{text:'Mashing',to:{name:'ProductionMashing'}},{text:'Mash bills',to:{name:'ProductionMashingMashBills'}}]},query:{to:'reports/production/ReportsMashingActivity',mashBillId:item.id}}"><v-list-item-title>Mashing activity report</v-list-item-title></v-list-item>
            <v-list-item v-if="company.allowAudit && company.permissions.includes('audit')" :to="{name:'Audit',query:{entityId:item.id}}"><v-list-item-title>Audit history</v-list-item-title></v-list-item>
            <template v-if="!readOnly">
              <v-divider/>
              <v-list-item :to="{name:'ProductionMashingMashBillsEdit',params:{mashBillId:item.id}}"><v-list-item-title>Edit mash Bill</v-list-item-title></v-list-item>
              <v-list-item :to="{name:'ProductionMashingMashBillsEdit',params:{mashBillId:item.id,copy:true}}"><v-list-item-title>Copy mash Bill</v-list-item-title></v-list-item>
              <v-list-item @click="mashBill=item;showDelete=true"><v-list-item-title>Delete mash bill</v-list-item-title></v-list-item>
            </template>
          </v-list>
        </v-menu>
      </template>
      <template v-slot:item.duration="{value}">{{value | duration}}</template>
      <template v-slot:item.volume="{value}">{{value | number(0)}} litres</template>
      <template v-slot:expanded-item="{ headers,item }">
        <td :colspan="headers.length" >
          <v-card class="my-4" :class="expansionPanelClass">
            <v-simple-table dense class="pl-4 text-caption" :class="expansionPanelClass" style="width:50%">
              <thead>
                <tr>
                  <th :class="expansionPanelClass" width="60%">Input</th>
                  <th class="text-right" :class="expansionPanelClass" width="40%">Quantity</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item of item.items" :key="item.itemId">
                  <td class="text-caption">{{item.itemName}}</td>
                  <td class="text-caption text-right">{{item.quantity | number(item.itemPrecision)}} {{item.itemUnits}}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card>
        </td>
      </template>
    </purtrak-table>

    <delete-object v-model="showDelete" :name="mashBill.name" @delete="performDeleteMashBill"/>
  </div>
</template>

<script>
import productionMashingPrintMash from '@/views/production/mashing/productionMashingPrintMash'

export default {
  name: 'ProductionMashingMashBills',
  mixins: [productionMashingPrintMash],
  data() {
    return {
      objectType: 'mash bills',
      _name: 'mashbills',
      filters: {
        status: '1',
        search: ''
      },
      preferences: {},
      mashBill: {},
      showDelete: false,
      columns: [],
      defaultColumns: [{ value: 'name', visible:true},
                       { value: 'volume', visible:true},
                       { value: 'duration', visible:true},
                       { value: 'createmash', visible:true}],
      headers: [{ text: 'MASH BILL', value: 'name', width:'40%',sortable:true},
                { text: 'VOLUME', value: 'volume', width:'25%',align:'right',sortable:true},
                { text: 'AVG DURATION', value: 'duration', width:'25%',sortable:true, omit:() => !this.preferences.mashDuration},
                { text: 'CREATE', value: 'createmash', width:'10%',sortable:false,align:'center',print:false}]
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    readOnly() {
      return !this.company.permissions.includes('production.write');
    },
    listFilters() {
      const filters = [];
      if (this.filters.status !== '2')
        filters.push({field:'active', comparator:'eq', value:this.filters.status == '1' ? 'true' : 'false'});
      if (this.filters.search)
        filters.push({field:'name', comparator:'contains', value:this.filters.search});
      return filters;
    }
  },
  async created() {
    const results = await this.$apiCall(`query ProductionMashingMashBills {
                                              mashingPreferences {
                                                mashDuration
                                              }
                                            }`);
    if (!results.error)
      this.preferences = results.data.mashingPreferences;
    this.loading = false;
  },
  methods: {
    async expandMashBill(item, value) {
      if (value && !item.items) {
        this.loading = true;
        const results = await this.$apiCall(`query ProductionMashingMashBills($id:ID!) {
                                                  mashBill(id:$id) {
                                                    items{itemId,itemName,itemUnits,itemPrecision,quantity},
                                                  }
                                                }`,
                                            {id:item.id});
        this.$set(item, 'items', results.data.mashBill.items);
        this.loading = false;
      }
    },
    async performDeleteMashBill() {
      this.loading = true;
      const results = await this.$apiCall(`mutation ProductionMashingMashBills($id: ID!) {
                                             objectDelete(id:$id)
                                            }`,
                                          {id:this.mashBill.id});
      this.loading = false;
      if (!results.error) {
        this.showDelete = false;
        this.$store.commit('setError', {type:'success',message:`Deleted mash bill ${this.mashBill.name}`});
        this.$refs.table?.buildList();
      }
    },
  }
}
</script>